import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2, Sparkles } from "lucide-react";
import { API } from "../lib/api";

const SUGGESTIONS = [
  "Which modalities does X-Gen support?",
  "How accurate is tumor detection?",
  "Does it integrate with our PACS?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello — I'm X-Gen, the platform assistant. Ask me anything about our AI diagnostics, accuracy, deployment or security." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionRef.current }),
      });
      if (!resp.ok) throw new Error("Chat request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = JSON.parse(p.slice(6));
          if (payload.session_id) sessionRef.current = payload.session_id;
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The assistant is temporarily unavailable. Please try again shortly." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 260, damping: 20 }}
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-widget-toggle"
        aria-label="Open X-Gen assistant"
        className="fixed bottom-6 right-6 z-[70] w-14 h-14 bg-brand text-white flex items-center justify-center hover:bg-[#0F172A] hover:text-white transition-colors duration-300 shadow-[0_0_30px_rgba(0,122,255,0.35)]"
      >
        {open ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[70] w-[min(380px,calc(100vw-3rem))] bg-surface border border-slate-200 flex flex-col overflow-hidden rounded-2xl shadow-[0_8px_30px_rgba(15,23,42,0.12)]"
            data-testid="chat-widget-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-white">
              <span className="w-8 h-8 bg-brand flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </span>
              <div>
                <p className="font-display font-bold text-sm tracking-tight">X-Gen Assistant</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">Online</p>
              </div>
            </div>

            <div ref={scrollRef} className="h-80 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-brand text-white" : "bg-slate-100 border border-slate-200 text-slate-700"
                    }`}
                    data-testid={`chat-message-${m.role}-${i}`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin" /> : null)}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="space-y-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`chat-suggestion-${s.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}
                      className="block w-full text-left font-mono text-[11px] uppercase tracking-[0.1em] text-mute border border-slate-200 px-3 py-2.5 hover:border-brand hover:text-slate-900 transition-colors duration-300"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex border-t border-slate-200"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about X-Gen..."
                data-testid="chat-input"
                className="flex-1 bg-transparent px-4 py-4 text-sm outline-none placeholder:text-mute"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                className="px-5 text-brand hover:text-[#0F172A] transition-colors duration-300 disabled:opacity-40"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
